/**
 * Pure diff for ServiceDesk P1 / P2 tickets.
 *
 * The priority names are READ OFF THE LIVE PORTAL (GET /api/v3/priorities on
 * ictportal, 2026-10-12), not guessed:
 *   1 "4 - Low", 2 "3 - Normal", 3 "2 - High", 4 "1 - Immediate", 301 "5 - Long Term".
 * So P1 is "1 - Immediate" and P2 is "2 - High". The leading digit is what is
 * matched, so a renamed label ("1 - Critical") still counts as P1.
 *
 * Announces a ticket the first time it is seen at P1/P2 — whether it was raised
 * that way or ESCALATED to it later (the sweep reads by last-updated time, so a
 * P3 bumped to P1 appears). A P2 later raised to P1 is announced again; a P1
 * dropped to P2 is not. Tickets already Closed/Resolved/Cancelled when first
 * seen are recorded but not announced — nobody needs to be paged about a P2
 * that was opened and closed inside the two-minute gap.
 *
 * The seen-map is NOT rebuilt from the window (unlike the security detectors):
 * a P1 that drops out of the read window and is updated again next week must
 * not page everybody a second time. Entries are pruned after `retentionMs`
 * instead, which keeps it bounded.
 */
/** 1 for P1, 2 for P2, null for anything else. */
export function ticketPriorityLevel(priority) {
    const m = /^\s*P?\s*([12])\b/i.exec(String(priority ?? ""));
    if (!m)
        return null;
    return m[1] === "1" ? 1 : 2;
}
const CLOSED = new Set(["closed", "resolved", "cancelled", "canceled", "rejected"]);
export function detectPriorityTicketChanges(prev, tickets, nowMs, retentionMs = 30 * 24 * 60 * 60 * 1000) {
    const seen = new Map();
    if (prev)
        for (const [id, e] of prev)
            if (nowMs - e.at < retentionMs)
                seen.set(id, { ...e });
    const list = Array.isArray(tickets) ? tickets.filter((t) => t && t.id) : [];
    const fresh = [];
    const pickedUp = [];
    for (const t of list) {
        const level = ticketPriorityLevel(t.priority);
        if (!level)
            continue;
        const tech = String(t.technicianName ?? "").trim() || String(t.technicianEmail ?? "").trim();
        const before = seen.get(t.id);
        // Picked up: a ticket already known (alerted or baselined) that has just
        // got a technician. Reported once; a later re-assignment is the
        // "assigned to you" feed's business, not this one's.
        if (before && tech && !before.pickedUpBy) {
            before.pickedUpBy = tech;
            if (prev)
                pickedUp.push({ id: t.id, level: before.level <= level ? before.level : level, technicianName: tech, technicianEmail: String(t.technicianEmail ?? "").trim() });
        }
        if (before && before.level <= level)
            continue;
        // New, or escalated to a higher priority. Carry the pickup over so an
        // escalation of an already-assigned ticket does not report it again.
        seen.set(t.id, { level, at: nowMs, ...(tech ? { pickedUpBy: before?.pickedUpBy || tech } : {}) });
        if (!prev)
            continue;
        if (CLOSED.has(String(t.status ?? "").trim().toLowerCase()))
            continue;
        fresh.push({
            id: t.id,
            subject: t.subject || `Ticket ${t.id}`,
            priority: t.priority ?? "",
            level,
            status: t.status ?? "",
            group: t.group ?? "",
            requester: t.requester ?? "",
            escalated: Boolean(before),
            createdTime: t.createdTime ?? null,
            technicianName: tech
        });
    }
    // P1 first, then oldest first, so the most important lands at the top.
    fresh.sort((a, b) => a.level - b.level || (a.createdTime ?? 0) - (b.createdTime ?? 0));
    return { fresh, seen, pickedUp };
}
export const PRIORITY_TICKET_FEED_VERSION = 1;
export function serializePriorityTicketSeen(seen) {
    return { tickets: Object.fromEntries(seen), feedVersion: PRIORITY_TICKET_FEED_VERSION };
}
export function deserializePriorityTicketSeen(raw) {
    if (!raw || typeof raw !== "object")
        return null;
    const j = raw;
    if (j.feedVersion !== PRIORITY_TICKET_FEED_VERSION || !j.tickets || typeof j.tickets !== "object")
        return null;
    const map = new Map();
    for (const [id, v] of Object.entries(j.tickets)) {
        const e = v;
        if (e && Number.isFinite(e.level) && Number.isFinite(e.at)) {
            map.set(id, { level: e.level, at: e.at, ...(typeof e.pickedUpBy === "string" && e.pickedUpBy ? { pickedUpBy: e.pickedUpBy } : {}) });
        }
    }
    return map;
}
/**
 * Notification kinds that interrupt: they raise the urgent pop-up in the app
 * and are pushed to the desktop even when Omni is closed.
 *
 * One list, shared by the API (what to push) and the SPA (what pops up), so the
 * two cannot disagree about what counts as urgent.
 */
export const URGENT_NOTIFICATION_KINDS = new Set([
    "servicedesk-priority",
    "risky-user",
    "security-incident",
    "security-alert",
    "network-incident"
]);
/**
 * Kinds pushed to the desktop: the urgent ones, an approval waiting on you,
 * a ticket assigned to you, and a reply/update on one (standard, non-urgent).
 */
export const PUSH_NOTIFICATION_KINDS = new Set([
    ...URGENT_NOTIFICATION_KINDS,
    "approval-requested",
    "servicedesk-assigned",
    // An admin-written known-issue notice (modules/service-notices) — everyone
    // should hear about it even with Omni closed.
    "service-notice",
    "servicedesk-updated",
    // A new ticket for your team (ServiceDesk group with nobody assigned yet).
    "servicedesk-group-assigned",
    // A backup item failed or went overdue — standard, not urgent.
    "backup-failed"
]);
